import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import usePageMeta from "../../hooks/usePageMeta";
import {
    getAllStoresAction, createStoreAction, updateStoreAction, deleteStoreAction,
    clearAdminErrors,
} from "../../redux/actions/adminActions";
import { ADMIN_STORE_MUTATE_RESET } from "../../redux/constants/adminConstants";

const EMPTY = { name: "", address: "", is_active: true };

const StoreManagement = () => {
    const dispatch = useDispatch();
    const { stores, loading } = useSelector(s => s.adminStores);
    const { loading: mutating, success, message, error } = useSelector(s => s.adminStoreMutate);

    const [modal, setModal] = useState(null); // null | { mode: 'create' | 'edit', store }
    const [form, setForm] = useState(EMPTY);
    const [deleteTarget, setDeleteTarget] = useState(null);

    usePageMeta("storeManagement");

    useEffect(() => {
        dispatch(getAllStoresAction());
    }, [dispatch]);

    useEffect(() => {
        if (success) {
            toast.success(message);
            dispatch({ type: ADMIN_STORE_MUTATE_RESET });
            setModal(null);
            setDeleteTarget(null);
            dispatch(getAllStoresAction());
        }
        if (error) {
            toast.error(error);
            dispatch(clearAdminErrors());
        }
    }, [success, message, error, dispatch]);

    const openCreate = () => { setForm(EMPTY); setModal({ mode: "create" }); };
    const openEdit = (store) => { setForm({ name: store.name, address: store.address || "", is_active: store.is_active }); setModal({ mode: "edit", store }); };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error("Store name is required"); return; }
        if (modal.mode === "create") dispatch(createStoreAction(form));
        else dispatch(updateStoreAction(modal.store.id, form));
    };

    const handleDelete = () => dispatch(deleteStoreAction(deleteTarget.id));

    return (
        <div className="pl-[1.5vmax] pr-[2.5vmax] mt-[1vmax] mb-[4vmax]">
            <div className="flex justify-between items-center bg-white p-[1.2vmax] rounded-[1vmax] px-[2vmax] mb-[2vmax]">
                <h2 className="text-[1.3vmax] font-bold">Store Management</h2>
                <button
                    onClick={openCreate}
                    className="bg-[var(--color-2)] hover:opacity-90 text-white text-[1vmax] py-[0.6vmax] px-[1.5vmax] rounded-[0.5vmax] flex items-center gap-[0.5vmax]"
                >
                    <i className="fa-solid fa-plus" /> Add Store
                </button>
            </div>

            <div className="bg-white shadow-1 rounded-2xl overflow-hidden">
                {loading ? (
                    <p className="text-center py-[4vmax] text-slate-400">Loading...</p>
                ) : stores.length === 0 ? (
                    <p className="text-center py-[4vmax] text-slate-400">No stores yet. Create your first store.</p>
                ) : (
                    <table className="w-full text-[1vmax]">
                        <thead className="bg-slate-50 border-b">
                            <tr>
                                {[
                                    { h: "#", align: "th-center" },
                                    { h: "Name", align: "th-text" },
                                    { h: "Address", align: "th-text" },
                                    { h: "Users", align: "th-center" },
                                    { h: "Status", align: "th-center" },
                                    { h: "Created", align: "th-text" },
                                    { h: "Actions", align: "th-center" },
                                ].map(({ h, align }) => (
                                    <th key={h} className={`${align} px-[1.5vmax] py-[1vmax] text-slate-600 font-semibold`}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {stores.map((s, i) => (
                                <tr key={s.id} className="border-b hover:bg-slate-50 transition-colors">
                                    <td className="px-[1.5vmax] py-[1vmax] td-center text-slate-400">{i + 1}</td>
                                    <td className="px-[1.5vmax] py-[1vmax] td-text font-semibold">{s.name}</td>
                                    <td className="px-[1.5vmax] py-[1vmax] td-text text-slate-500">{s.address || "—"}</td>
                                    <td className="px-[1.5vmax] py-[1vmax] td-center">
                                        <span className="bg-blue-50 text-blue-700 px-[0.8vmax] py-[0.2vmax] rounded-full font-semibold">{s.user_count}</span>
                                    </td>
                                    <td className="px-[1.5vmax] py-[1vmax] td-center">
                                        <span className={`px-[0.8vmax] py-[0.2vmax] rounded-full text-[0.85vmax] font-semibold ${s.is_active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                                            {s.is_active ? "Active" : "Inactive"}
                                        </span>
                                    </td>
                                    <td className="px-[1.5vmax] py-[1vmax] td-text text-slate-400">{new Date(s.created_at).toLocaleDateString()}</td>
                                    <td className="px-[1.5vmax] py-[1vmax] td-center">
                                        <div className="flex gap-[0.8vmax] justify-center">
                                            <button onClick={() => openEdit(s)} className="text-[var(--color-2)] hover:opacity-70 text-[1.1vmax]"><i className="fa-solid fa-pen" /></button>
                                            <button onClick={() => setDeleteTarget(s)} className="text-red-500 hover:opacity-70 text-[1.1vmax]"><i className="fa-solid fa-trash" /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Create / Edit Modal */}
            {modal && (
                <div className="fixed inset-0 bg-black/50 z-[999] flex-center">
                    <div className="bg-white rounded-2xl p-[2vmax] w-[90%] lg:w-[35%] shadow-xl">
                        <h3 className="font-bold text-[1.3vmax] mb-[1.5vmax]">
                            {modal.mode === "create" ? "Create New Store" : "Edit Store"}
                        </h3>
                        <form onSubmit={handleSubmit} className="flex flex-col gap-[1vmax]">
                            <div className="flex flex-col gap-[0.4vmax]">
                                <label className="text-[1vmax] font-semibold text-slate-600">Store Name <span className="text-red-500">*</span></label>
                                <input
                                    className="border border-slate-300 rounded-xl p-[0.8vmax] text-[1vmax] outline-[var(--color-2)]"
                                    value={form.name}
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                    placeholder="e.g. Downtown Store"
                                />
                            </div>
                            <div className="flex flex-col gap-[0.4vmax]">
                                <label className="text-[1vmax] font-semibold text-slate-600">Address</label>
                                <input
                                    className="border border-slate-300 rounded-xl p-[0.8vmax] text-[1vmax] outline-[var(--color-2)]"
                                    value={form.address}
                                    onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                                    placeholder="Store address (optional)"
                                />
                            </div>
                            {modal.mode === "edit" && (
                                <label className="flex items-center gap-[0.5vmax] text-[1vmax] cursor-pointer">
                                    <input type="checkbox" checked={!!form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                                    Active
                                </label>
                            )}
                            <div className="flex justify-end gap-[1vmax] mt-[0.5vmax]">
                                <button type="button" onClick={() => setModal(null)} className="px-[1.5vmax] py-[0.6vmax] rounded-lg bg-slate-100 text-[1vmax] hover:bg-slate-200">Cancel</button>
                                <button type="submit" disabled={mutating} className="px-[1.5vmax] py-[0.6vmax] rounded-lg bg-[var(--color-2)] text-white text-[1vmax] disabled:opacity-60">
                                    {mutating ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {deleteTarget && (
                <div className="fixed inset-0 bg-black/50 z-[999] flex-center">
                    <div className="bg-white rounded-2xl p-[2vmax] w-[90%] lg:w-[30%] shadow-xl text-center">
                        <i className="fa-solid fa-triangle-exclamation text-red-500 text-[3vmax] mb-[1vmax]" />
                        <h3 className="font-bold text-[1.2vmax] mb-[0.5vmax]">Delete Store</h3>
                        <p className="text-slate-500 text-[1vmax] mb-[1.5vmax]">Delete <strong>{deleteTarget.name}</strong>? This will remove all associated users and data.</p>
                        <div className="flex justify-center gap-[1vmax]">
                            <button onClick={() => setDeleteTarget(null)} className="px-[1.5vmax] py-[0.6vmax] rounded-lg bg-slate-100 text-[1vmax]">Cancel</button>
                            <button onClick={handleDelete} disabled={mutating} className="px-[1.5vmax] py-[0.6vmax] rounded-lg bg-red-500 text-white text-[1vmax] disabled:opacity-60">
                                {mutating ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StoreManagement;
